import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import "./legal.css";
import { ReactComponent as MainLogo } from "../../assets/signin/Logo.svg";
import { baseUrl } from "../../helpers/config";

// Public, no sign-in: Google Play requires a web page where somebody can delete
// their account without the app. It signs in with the email and password given
// here and deletes with that session, so the server has one deletion path
// (POST /api/user/deleteaccount) shared with the app.
//
// Plain axios rather than axiosInstance: that one attaches whatever session
// this browser has saved, and on a 401 redirects to /signin -- neither is right
// for a page acting on the account typed into it.
function DeleteAccount() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const messageFrom = (err, fallback) =>
    err?.response?.data?.message || fallback;

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Enter the email and password you sign in with.");
      return;
    }
    setBusy(true);
    try {
      let session;
      try {
        session = await axios.post(`${baseUrl}/api/user/signin`, {
          email: email.trim().toLowerCase(),
          password,
        });
      } catch (err) {
        const status = err?.response?.status;
        setError(
          status === 404 || status === 400
            ? "That email and password do not match an account."
            : messageFrom(err, "Could not reach SwitchFund. Try again.")
        );
        return;
      }

      await axios.post(
        `${baseUrl}/api/user/deleteaccount`,
        { password },
        { headers: { Authorization: `Bearer ${session.data.accessToken}` } }
      );

      // If this browser was signed in to the console as this person, that
      // session is dead now; do not leave it looking signed in.
      const saved = localStorage.getItem("user");
      if (saved && saved.includes(session.data.userModel?._id)) {
        ["accessToken", "refreshToken", "user", "organisation"].forEach((key) =>
          localStorage.removeItem(key)
        );
      }
      setPassword("");
      setDone(true);
    } catch (err) {
      setError(messageFrom(err, "Something went wrong. Your account was not deleted."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="legal-page">
      <div className="legal-column">
        <MainLogo className="legal-logo" />
        <h1>Delete your SwitchFund account</h1>

        {done ? (
          <div className="legal-done">
            <p>
              <strong>Your account has been deleted.</strong> You have been
              signed out on every device, and you can no longer sign in with
              that email and password.
            </p>
          </div>
        ) : (
          <>
            <p>
              You can also do this in the app: <strong>Profile → Delete account</strong>.
            </p>

            <h2>What is deleted</h2>
            <ul>
              <li>Your email address and password — you will not be able to sign in again</li>
              <li>Your phone number, address, age, gender and profile photo</li>
              <li>Your bank details and UPI ID</li>
              <li>Your membership of every fund, and your device's notification token</li>
            </ul>

            <h2>What your fund keeps</h2>
            <p>
              Your name, your member ID, and the fund's records of your
              savings, loans and repayments. These are part of the fund's
              accounts, which every other member's balances depend on. If you
              still owe the fund money, deleting your account does not cancel
              that. See the <Link to="/privacy">privacy policy</Link>.
            </p>

            <h2>This cannot be undone</h2>
            <form className="legal-form" onSubmit={submit}>
              <input
                type="email"
                placeholder="Email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <input
                type="password"
                placeholder="Password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <label className="legal-check">
                <input
                  type="checkbox"
                  checked={understood}
                  onChange={(e) => setUnderstood(e.target.checked)}
                />
                I understand my account will be deleted permanently.
              </label>
              {error && <p className="legal-error">{error}</p>}
              <button className="legal-danger" disabled={busy || !understood}>
                {busy ? "Deleting…" : "Delete my account"}
              </button>
            </form>
            <p>
              Forgotten your password?{" "}
              <Link to="/forgotpassword">Reset it</Link> first. Trouble?
              Write to{" "}
              <a href="mailto:youthalliancefund@gmail.com">youthalliancefund@gmail.com</a>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default DeleteAccount;
